/** @jsx jsx */
import React, { type ReactNode } from 'react';

// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import { SpotlightTarget as DeprecatedSpotlightTarget } from '@atlaskit/onboarding';

import { cssMap, jsx } from '@atlaskit/css';
import { fg } from '@atlassian/jira-feature-gating';
import { targetTranslation } from './translations/targetTranslation.tsx';

const styles = cssMap({
	root: {
		width: 'fit-content',
	},
	fullWidth: {
		width: '100%',
	},
});

export interface SpotlightTargetProps {
	/**
	 * A single child.
	 */
	children: ReactNode;
	/**
	 * The name to reference from Spotlight.
	 */
	name: string;
	/**
	 * Stretch to fill the full width of a parent container.
	 */
	isFullWidth?: boolean;
}

/**
 * __Spotlight target__
 *
 * A spotlight target marks a component to be used for introducing new features to users through focused messages or multi-step tours.
 *
 */
const SpotlightTarget: {
	({ children, name, isFullWidth }: SpotlightTargetProps): React.JSX.Element;
	displayName: string;
} = ({ children, name, isFullWidth = false }: SpotlightTargetProps): React.JSX.Element => {
	const id = targetTranslation(name);

	if (fg('jira_ads_spotlight_migration')) {
		return (
			<div css={[styles.root, isFullWidth && styles.fullWidth]} id={id}>
				{children}
			</div>
		);
	}

	return <DeprecatedSpotlightTarget name={name}>{children}</DeprecatedSpotlightTarget>;
};

SpotlightTarget.displayName = 'SpotlightTarget';

export { SpotlightTarget };
